/**
 * Ticket Template System - Type Definitions
 *
 * Centralized type definitions for ticket form configurations.
 * Templates define field visibility, requirements, defaults, and sample content
 * based on ticket_type_group and ticket_type_key.
 */

import { TicketTypeGroup } from '../types/response'

/**
 * Context passed to conditional functions for field visibility/requirements
 */
export interface TicketContext {
    group: string
    key?: string
    priority?: string
    serviceId?: number
    customerId?: number
    hasCustomerJourneyId?: boolean
}

/**
 * Service date field variant types
 * - 'creating': Only start date, editable
 * - 'cancelled': Both dates, start disabled, end editable
 * - 'suspend': Both dates + suspend_until, start disabled
 * - 'poc': Both dates, both editable (for POC services)
 * - 'default': Both dates, both editable
 */
export type ServiceDateVariant =
    | 'creating'
    | 'cancelled'
    | 'suspend'
    | 'poc'
    | 'default'

/**
 * Configuration for a single form field
 */
export interface TicketTemplateField {
    /** Field is required for form submission */
    required: boolean
    /** Field is visible in the form */
    visible: boolean
    /** Field is disabled (read-only) */
    disabled?: boolean
    /** Default value for the field */
    defaultValue?: unknown
    /** Conditional function for dynamic visibility */
    condition?: (context: TicketContext) => boolean
    /** Variant for service date fields */
    variant?: ServiceDateVariant
}

/**
 * All field configurations for a ticket template
 */
export interface TicketTemplateFields {
    /** Customer selection field */
    customer: TicketTemplateField
    /** Service/product selection field */
    service: TicketTemplateField
    /** Ticket content/description field */
    content: TicketTemplateField
    /** Priority selection field */
    priority: TicketTemplateField
    /** Major incident checkbox */
    marjorable: TicketTemplateField
    /** Affected area/scope field */
    affected: TicketTemplateField
    /** Meeting-related fields group */
    meeting: TicketTemplateField
    /** Service date fields group */
    serviceDates: TicketTemplateField
    /** Time reply field */
    timeReply: TicketTemplateField
    /** Estimated completion date field */
    estimatedCompletion: TicketTemplateField
    /** Assignee fields group */
    assignees: TicketTemplateField
}

/**
 * Service filter configuration for specific ticket types
 */
export interface ServiceFilter {
    is_poc?: boolean
}

/**
 * Validation rule configuration
 */
export interface ValidationRule {
    required?: boolean
    min?: number
    max?: number
    pattern?: RegExp
    message?: string
}

/**
 * Complete ticket template configuration
 */
export interface TicketTemplate {
    /** Ticket type group this template applies to */
    group: TicketTypeGroup
    /** Optional specific ticket type key */
    key?: string
    /** Field configurations */
    fields: TicketTemplateFields
    /** Sample content for the ticket content field */
    sampleContent: string
    /** Default values applied when this template is selected */
    defaults: Record<string, unknown>
    /** Validation rules for fields */
    validations: Record<string, ValidationRule>
    /** Service filter for service selection */
    serviceFilter?: ServiceFilter
}

/**
 * Partial template for key-specific overrides
 */
export type TicketTemplateOverride = Partial<
    Omit<TicketTemplate, 'fields'> & {
        fields: Partial<TicketTemplateFields>
    }
>

/**
 * Default field configuration - all fields visible and optional
 */
export const DEFAULT_FIELD: TicketTemplateField = {
    required: false,
    visible: true,
    disabled: false,
}

/**
 * Hidden field configuration
 */
export const HIDDEN_FIELD: TicketTemplateField = {
    required: false,
    visible: false,
    disabled: false,
}
